import {
  Component,
  input,
  output,
  signal,
  computed,
  effect,
  untracked,
  ChangeDetectionStrategy,
  HostListener,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { ScrollingModule, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Subscription } from 'rxjs';
import {
  SoTableHeader,
  SoTableButton,
  SoTableLeftButton,
  SoTableStyle,
  SoTableHeaderStyle,
  SoSortDirection,
  SoTableSwitchEvent,
  SoTablePaginationType,
  SoTableTokenPaginationState,
  SoTableTokenPaginationEvent,
  SoTableVariableSelectionEvent,
  SoTableHeadersFormStatus,
  SoTableHighlightedText,
  SoTableDensity,
  SoTableVariableStatus,
  SoTableVariableStatusMap
} from './so-table.interfaces';
import { SoButtonGradientUI } from '../so-button-gradient/so-button-gradient.component';
import { SoBadgeColors, SoBadgeUI } from '../so-badge/so-badge.component';
import { SoSwitchUI } from '../so-switch/so-switch.component';
import { SoSvgIconUI } from '../so-svg-icon/so-svg-icon.component';
import { SoPremiumIconUI } from '../so-premium-icon/so-premium-icon.component';
import { SoPaginationUI } from '../so-pagination/so-pagination.component';
import { SoSelectUI } from '../so-select/so-select.component';
import { SoSelectItem, SoSelectKey } from '../interfaces/select.interfaces';
import { SoInputUI } from '../so-input/so-input.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SoRowActionsMenuUI } from '../so-row-actions-menu/so-row-actions-menu.component';
import { SoTokenPaginationUI } from '../so-token-pagination/so-token-pagination.component';

@Component({
  selector: 'so-table-ui',
  standalone: true,
  imports: [ CommonModule, NgComponentOutlet, ScrollingModule, SoButtonGradientUI, SoBadgeUI, SoSwitchUI, SoSvgIconUI, SoPremiumIconUI, SoPaginationUI, SoSelectUI, SoInputUI, MatTooltipModule, SoRowActionsMenuUI, SoTokenPaginationUI ],
  templateUrl: './so-table.component.html',
  styleUrl: './so-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoTableUI implements AfterViewInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  @ViewChild(CdkVirtualScrollViewport) private viewport?: CdkVirtualScrollViewport;
  @ViewChild('tableHead', { static: false }) private tableHeadRef?: ElementRef<HTMLTableSectionElement>;
  @ViewChild('tableContent', { static: false }) private tableContentRef?: ElementRef<HTMLDivElement>;
  @ViewChild('tfoot', { static: false }) private tfootRef?: ElementRef<HTMLTableSectionElement>;
  @ViewChild('tableSearchInput') private searchInputRef?: SoInputUI;

  private theadResizeObserver?: ResizeObserver;
  private contentResizeObserver?: ResizeObserver;
  private contentScrollListener?: () => void;
  private columnWidthSyncFn?: () => void;
  private stickyLeftSyncFn?: (scrollLeft?: number) => void;
  private renderedRangeSub?: Subscription;

  // Data inputs
  tableData = input.required<Record<string, any>[]>();
  tableHeaders = input.required<SoTableHeader[]>();

  // Table configuration
  tableStyle = input<SoTableStyle>('default');
  headerStyle = input<SoTableHeaderStyle>('new');
  showInnerBorders = input<boolean>(true);
  textWrap = input<boolean>(true);
  striped = input<boolean>(false);
  containerCustomStyle = input<string>('');
  fixHeadCustomStyle = input<string>('');
  syncHorizontalScroll = input<boolean>(false);
  formFieldsVariant = input<'compact' | 'classic'>('compact');

  // Sticky edge columns (dynamic)
  stickyLeftColumns = input<number>(0);
  stickyRightColumns = input<number>(0);

  // Header section
  showTableHeader = input<boolean>(false);
  tableTitle = input<string>('');
  tableSubtitle = input<string>('');
  tableHeaderIcon = input<string>('');
  tableHeaderIconColor = input<string>('--so-primary');

  // Search
  showTableSearch = input<boolean>(false);
  tableSearchPlaceholder = input<string>('Buscar...');
  searchText = input<string>('');

  // Action button in header
  showTableActionButton = input<boolean>(false);
  tableActionButtonText = input<string>('');
  tableActionButtonIcon = input<string>('');
  tableActionButtonDisabled = input<boolean>(false);
  tableActionButtonTooltip = input<string>('');

  // Row configuration
  enableRowSelection = input<boolean>(false);
  density = input<SoTableDensity>('default');
  rowsHeight = input<string | undefined>(undefined);
  headersHeight = input<string>('');
  rowColorFunction = input<((item: any) => string) | undefined>(undefined);
  rowBorderFunction = input<((item: any) => string) | undefined>(undefined);

  // Sticky column
  firstColumnStickyLeft = input<boolean>(false);
  firstColumnPaddingLeft = input<string>('5%');
  lastColumnPaddingRight = input<string>('1rem');

  // Headers styling
  headersColorIsPrimary = input<boolean>(true);
  headersCaseIsUpper = input<boolean>(true);

  // Column visibility control (consumer-driven, e.g. so-column-picker-ui)
  hiddenColumns = input<string[]>([]);

  // Actions column
  isActionsEnabled = input<boolean>(false);
  editAction = input<boolean>(true);
  deleteAction = input<boolean>(true);
  customButtons = input<SoTableButton[]>([]);
  buttonsContainerWidth = input<string>('auto');
  buttonsContainerPaddingLeft = input<string | null>(null);
  actionsDisplay = input<'inline' | 'menu'>('inline');

  // Switches column
  showSwitches = input<boolean>(false);
  switchHeader = input<string>('');
  switchIdentifier = input<string>('');
  switchLabelLeft = input<string>('No');
  switchLabelRight = input<string>('Sí');
  enableAllSwitches = input<boolean>(true);
  disabledSwitchTooltip = input<string>('El switch está deshabilitado');
  rowSwitchDisabledFunction = input<((item: any) => boolean) | undefined>(undefined);
  rowSwitchDisabledTooltipFunction = input<((item: any) => string) | undefined>(undefined);
  switchRowVisibilityFN = input<((item: any) => boolean) | undefined>(undefined);
  switchRowResolverFN = input<((item: any) => boolean) | undefined>(undefined);
  switchAlign = input<'start' | 'center' | 'end'>('center');

  // Icons column
  showIcons = input<boolean>(false);
  iconsHeader = input<string>('');
  iconsIdentifier = input<string>('');

  // Pagination
  enablePagination = input<boolean>(false);
  paginationType = input<SoTablePaginationType>('classic');
  itemsPerPage = input<number>(15);
  currentPage = input<number>(1);
  loadingItems = input<boolean>(false);

  // Token Pagination
  tokenPaginationState = input<SoTableTokenPaginationState>({
    pageCounter: 0,
    pageSize: 10,
    nextToken: '',
    prevToken: ''
  });
  pageSizeOptions = input<number[]>([10, 25, 50, 100]);

  // Footer
  tableFeet = input<Record<string, any> | null>(null);
  footerHighlight = input<boolean>(true);
  paddingBottomClass = input<0 | 1 | 2 | 3 | 4 | 5>(5);
  borderRadius = input<boolean>(true);

  // Save/Left buttons
  enableSaveButton = input<boolean>(false);
  enableLeftButton = input<boolean>(false);
  leftButtonData = input<SoTableLeftButton>({ text: '', icon: '', disabled: true, tooltip: '' });

  // Lang
  lang = input<string>('');

  // Virtual Scroll (siempre activo)
  virtualItemSize = input<number | undefined>(undefined);
  virtualMinBufferPx = input<number>(240);
  virtualMaxBufferPx = input<number>(720);

  // Backward compatibility inputs
  checkTextTruncated = input<boolean>(true);

  // Variable Selection Headers
  enableVariableSelection = input<boolean>(false);
  enableEditableHeaders = input<boolean>(false);
  variablesCatalog = input<SoSelectItem[]>([]);
  takenVariables = input<SoSelectKey[]>([]);
  variableSelections = input<Record<string, SoSelectKey>>({});

  // Variable Status Headers
  enableVariableStatus = input<boolean>(false);
  variableStatusMap = input<SoTableVariableStatusMap>({});
  highlightedColumn = input<string | null>(null);
  unmappedStatusText = input<string>('sin mapear');

  // Outputs
  rowClickedEvent = output<Record<string, any> | null>();
  switchEvent = output<SoTableSwitchEvent>();
  editEvent = output<Record<string, any>>();
  deleteEvent = output<Record<string, any>>();
  saveEvent = output<Record<string, any>[]>();
  leftButtonEvent = output<void>();
  tableActionButtonClicked = output<void>();
  tableSearchChanged = output<string>();
  pageChanged = output<number>();
  sortChanged = output<{ field: string; direction: SoSortDirection }>();

  // Token pagination outputs
  nextPageRequested = output<SoTableTokenPaginationEvent>();
  previousPageRequested = output<SoTableTokenPaginationEvent>();
  pageSizeChanged = output<number>();

  // Variable Selection Headers outputs
  variableSelected = output<SoTableVariableSelectionEvent>();
  variableDeselected = output<SoTableVariableSelectionEvent>();
  variableHeaderEdited = output<SoTableVariableSelectionEvent>();
  headersFormStatus = output<SoTableHeadersFormStatus>();
  columnHovered = output<string | null>();

  // Internal state
  protected internalData = signal<Record<string, any>[]>([]);
  protected filteredData = signal<Record<string, any>[]>([]);
  protected selectedRow = signal<Record<string, any> | null>(null);
  protected sortField = signal<string>('');
  protected sortDirection = signal<SoSortDirection>(null);
  protected internalCurrentPage = signal<number>(1);
  protected internalSearchText = signal<string>('');
  protected truncateCache = new WeakMap<HTMLElement, boolean>();
  // Per-column cumulative pixel offset for sticky-left/right columns (keys: 'actions',
  // 'switch', 'icons', or header.dataName). Measured from the DOM — see updateStickyOffsets().
  protected stickyLeftOffsets = signal<Record<string, number>>({});
  protected stickyRightOffsets = signal<Record<string, number>>({});
  protected scrollbarWidthPx = signal<number>(0);
  private searchDebounceId: ReturnType<typeof setTimeout> | null = null;
  private viewportReady = false;
  private pendingViewportUpdate = false;

  // Variable Selection Headers state
  protected headerSelectValues = signal<Record<string, SoSelectKey>>({});
  protected headerInputValues = signal<Record<string, string>>({});
  protected headerInputErrors = signal<Record<string, boolean>>({});
  private previousVariableSelections = new Map<string, SoSelectKey>();

  // Computed values
  protected firstColumnIndex = computed(() => {
    const headers = this.tableHeaders();
    return Math.max(0, headers.findIndex(h => this.shouldShowColumn(h)));
  });

  protected lastColumnIndex = computed(() => {
    const headers = this.tableHeaders();
    for (let i = headers.length - 1; i >= 0; i--) {
      if (this.shouldShowColumn(headers[i])) {
        return i;
      }
    }
    return 0;
  });

  protected shownHeaders = computed(() => this.tableHeaders().filter(h => this.shouldShowColumn(h)));

  protected isActionsStickyLeft = computed(() =>
    this.isActionsEnabled() && this.stickyLeftColumns() > 0 && !this.isActionsStickyRight()
  );

  protected isActionsStickyRight = computed(() =>
    this.isActionsEnabled() && this.stickyRightColumns() > 0
  );

  protected stickyLeftDataNames = computed(() => {
    // Only reserve a slot for actions when it actually renders sticky-left — when actions is
    // sticky-right instead (this ticket's case), it shouldn't steal budget from data columns.
    const actionsSlot = this.isActionsStickyLeft() ? 1 : 0;
    const dataSlots = Math.max(0, this.stickyLeftColumns() - actionsSlot);
    return new Set(this.shownHeaders().slice(0, dataSlots).map(h => h.dataName));
  });

  protected isIconsStickyRight = computed(() => {
    // Same budget as stickyRightDataNames: the actions/kebab column (when sticky-right)
    // occupies a slot before icons gets a chance at one.
    const actionsSlot = this.isActionsStickyRight() ? 1 : 0;
    return this.showIcons() && this.stickyRightColumns() > actionsSlot;
  });

  protected isSwitchStickyRight = computed(() => {
    const actionsSlot = this.isActionsStickyRight() ? 1 : 0;
    const iconsSlot = this.showIcons() ? 1 : 0;
    return this.showSwitches() && this.stickyRightColumns() > actionsSlot + iconsSlot;
  });

  protected stickyRightDataNames = computed(() => {
    // Mirrors stickyLeftDataNames: only reserve a slot for actions when it actually
    // renders sticky-right, so a data column doesn't also get pinned there and steal
    // the offset that should belong solely to the actions/kebab column.
    const actionsSlot = this.isActionsStickyRight() ? 1 : 0;
    const iconsSlot = this.showIcons() ? 1 : 0;
    const switchSlot = this.showSwitches() ? 1 : 0;
    const dataSlots = Math.max(0, this.stickyRightColumns() - actionsSlot - iconsSlot - switchSlot);
    const shown = this.shownHeaders();
    return new Set(shown.slice(Math.max(0, shown.length - dataSlots)).map(h => h.dataName));
  });

  protected hasStickyColumns = computed(() =>
    this.stickyLeftColumns() > 0 || this.stickyRightColumns() > 0
  );

  protected displayedData = computed(() => {
    const data = this.filteredData();
    if (!this.enablePagination()) {
      return data;
    }
    const page = this.internalCurrentPage();
    const perPage = this.itemsPerPage();
    const startIndex = (page - 1) * perPage;
    return data.slice(startIndex, startIndex + perPage);
  });

  protected totalItems = computed(() => this.filteredData().length);

  protected totalPages = computed(() => {
    const total = this.totalItems();
    const perPage = this.itemsPerPage();
    return Math.ceil(total / perPage);
  });

  protected paginationInfo = computed(() => {
    const page = this.internalCurrentPage();
    const perPage = this.itemsPerPage();
    const total = this.totalItems();
    const start = Math.min((page - 1) * perPage + 1, total);
    const end = Math.min(page * perPage, total);
    return { start, end, total };
  });

  protected effectiveRowsHeight = computed(() => {
    const explicit = this.rowsHeight();
    if (explicit !== undefined) return explicit;
    return this.density() === 'dense' ? '31px' : '45px';
  });

  protected effectiveVirtualItemSize = computed(() => {
    const explicit = this.virtualItemSize();
    if (explicit !== undefined) return explicit;
    // Same proportional slack as the default density (48/45), applied to the dense row height (31px).
    return this.density() === 'dense' ? 33 : 48;
  });

  protected virtualViewportHeight = computed(() => {
    const rows = this.displayedData().length;
    const itemSize = this.effectiveVirtualItemSize();
    const maxVisibleRows = 10;
    // Keep a reasonable minimum height for the empty state or very few rows
    const visibleRows = rows === 0 ? 3 : Math.min(rows, maxVisibleRows);
    return visibleRows * itemSize;
  });

  protected colspan = computed(() => {
    let count = this.tableHeaders().filter(h => this.shouldShowColumn(h)).length;
    if (this.isActionsEnabled()) count++;
    if (this.showSwitches()) count++;
    if (this.showIcons()) count++;
    return count;
  });

  constructor() {
    // Sync external data with internal state
    effect(() => {
      const data = this.tableData();
      if (data) {
        const cloned = this.cloneData(data);
        this.internalData.set(cloned);
        const searchText = untracked(() => this.internalSearchText());
        this.filteredData.set(this.filterData(cloned, searchText));
      }
    }, {  });

    // Sync external current page
    effect(() => {
      const page = this.currentPage();
      untracked(() => {
        const normalizedPage = this.clampPage(page);
        if (normalizedPage !== this.internalCurrentPage()) {
          this.internalCurrentPage.set(normalizedPage);
        }
      });
    }, {  });

    effect(() => {
      const page = this.internalCurrentPage();
      const totalItems = this.filteredData().length;
      const pageSize = this.itemsPerPage();
      const normalizedPage = this.clampPage(page, totalItems, pageSize);

      if (normalizedPage !== page) {
        this.internalCurrentPage.set(normalizedPage);
      }
    }, {  });

    // Sync external search text
    effect(() => {
      const search = this.searchText();
      untracked(() => {
        if (search !== this.internalSearchText()) {
          this.internalSearchText.set(search);
          this.applySearch(search);
          this.searchInputRef?.writeValue(search);
        }
      });
    }, {  });

    // Sync variable selections from input
    effect(() => {
      const selections = this.variableSelections();
      const headers = this.tableHeaders();
      if (this.enableVariableSelection()) {
        this.initializeHeaderSelections(headers, selections);
      }
    }, {  });

    // Initialize header input values
    effect(() => {
      const headers = this.tableHeaders();
      if (this.enableEditableHeaders()) {
        this.initializeHeaderInputs(headers);
      }
    }, {  });

    // Sync column widths when data OR the set of visible columns changes (for horizontal
    // scroll sync). hiddenColumns() must be read here even though it isn't used directly —
    // toggling a column in the picker changes the thead's rendered widths, and without this
    // read the effect never re-fires, leaving body cells at their stale pre-toggle widths.
    effect(() => {
      this.hiddenColumns();
      if (this.syncHorizontalScroll() || this.hasStickyColumns()) {
        const data = this.displayedData();
        if (data && data.length > 0) {
          // Esperar a que el DOM se actualice antes de sincronizar
          setTimeout(() => {
            if (this.columnWidthSyncFn) {
              this.columnWidthSyncFn();
            }
          }, 0);
        }
      }
    });
  }

  // Public methods
  shouldShowColumn(header: SoTableHeader): boolean {
    const showColumn = header.showColumn === undefined
      ? true
      : typeof header.showColumn === 'function'
        ? header.showColumn()
        : header.showColumn;
    return showColumn && !this.hiddenColumns().includes(header.dataName);
  }

  getPropertyValue(item: any, path: string): any {
    return path.split('.').reduce((o, p) => o?.[p], item);
  }

  isTextTruncated(element: HTMLElement): boolean {
    if (!element || !this.checkTextTruncated()) return false;
    const cached = this.truncateCache.get(element);
    if (cached !== undefined) return cached;
    const isTruncated = element.clientWidth < element.scrollWidth ||
                        element.clientHeight < element.scrollHeight;
    this.truncateCache.set(element, isTruncated);
    return isTruncated;
  }

  resolveIconColor(color: string): string {
    if (!color) return '';
    if (color.startsWith('--')) return `var(${color})`;
    return color;
  }

  resolveIconSize(size: number | undefined): string {
    if (!size || size < 1) return '18px';
    return size + 'px';
  }

  resolveIconSizeNum(size: number | undefined): number {
    if (!size || size < 1) return 18;
    return size;
  }

  isButtonDisabled(button: SoTableButton, item: any): boolean {
    if (typeof button.disabled === 'function') return button.disabled(item);
    return !!button.disabled;
  }

  isButtonVisible(button: SoTableButton, item: any): boolean {
    if (button.show === undefined) return true;
    if (typeof button.show === 'function') return button.show(item);
    return !!button.show;
  }

  isBadgeVisible(badge: SoTableHeader['badge'], item: any) {
    if (badge === undefined || badge.show === undefined) return true;
    if (typeof badge.show === 'function') return badge.show(item);
    return badge.show;
  }

  isHeaderButtonDisabled(header: SoTableHeader, item: any): boolean {
    if (!header.isButton) return false;
    if (typeof header.isButton.disabled === 'function') return header.isButton.disabled(item);
    return !!header.isButton.disabled;
  }

  isHeaderButtonVisible(header: SoTableHeader, item: any): boolean {
    if (!header.isButton) return false;
    if (typeof header.isButton.show === 'function') return header.isButton.show(item);
    return !!header.isButton.show;
  }

  onHeaderButtonClick(header: SoTableHeader, item: any, event: Event): void {
    event.stopPropagation();
    if (header.isButton?.action) {
      header.isButton.action(item);
    }
  }

  getTooltipContent(button: SoTableButton, item: any): string {
    if (typeof button.tooltip === 'function') return button.tooltip(item) ?? '';
    return button.tooltip ?? '';
  }

  isSwitchDisabled(item: any): boolean {
    const fn = this.rowSwitchDisabledFunction();
    if (fn) return fn(item);
    return !this.enableAllSwitches() || item.disabledSwitch;
  }

  getSwitchTooltip(item: any): string {
    if (!this.isSwitchDisabled(item)) return '';
    const fn = this.rowSwitchDisabledTooltipFunction();
    if (fn) return fn(item);
    return this.disabledSwitchTooltip();
  }

  isSwitchVisible(item: any): boolean {
    const fn = this.switchRowVisibilityFN();
    if (fn) return fn(item);
    return true;
  }

  getSwitchStatus(item: any): boolean {
    const resolver = this.switchRowResolverFN();
    if (resolver) return !!resolver(item);
    const identifier = this.switchIdentifier();
    if (identifier) return !!item[identifier];
    return false;
  }

  getBadgeColor(colorVar: string): any {
    // Map CSS variable names to badge color types
    const colorMap: Record<string, string> = {
      '--so-primary': 'primary',
      '--so-secondary': 'secondary',
      '--so-success': 'success',
      '--so-success-thick': 'success',
      '--so-danger': 'danger',
      '--so-warning': 'warning',
      '--so-warning-thick': 'warning',
      '--so-info': 'info',
      '--so-gray': 'grey',
      '--so-primary-light': 'primary-light',
      '--so-secondary-light': 'secondary-light',
      '--so-success-light': 'success-light',
      '--so-danger-light': 'danger-light',
      '--so-warning-light': 'warning-light',
      '--so-info-light': 'info-light',
      '--so-gray-light': 'grey-light',
    };
    return colorMap[colorVar] || 'grey';
  }

  getBadgeColors(colors: SoBadgeColors | undefined): SoBadgeColors | undefined {
    if (colors) {
      colors.text = this.cssValue(colors.text) ?? colors.text;
      colors.background = this.cssValue(colors.background) ?? colors.background;
    }
    return colors;
  }

  // Event handlers
  onRowClick(item: Record<string, any>): void {
    const isAlreadySelected = this.selectedRow() === item;
    const newSelection = isAlreadySelected ? null : item;
    this.selectedRow.set(newSelection);
    this.rowClickedEvent.emit(newSelection);
  }

  onEditRow(item: Record<string, any>, event: Event): void {
    event.stopPropagation();
    this.editEvent.emit(item);
  }

  onDeleteRow(item: Record<string, any>, event: Event): void {
    event.stopPropagation();
    this.deleteEvent.emit(item);
  }

  onCustomButtonClick(button: SoTableButton, item: any, event: Event): void {
    event.stopPropagation();
    button.action(item);
  }

  // Item-agnostic on purpose: memoized so it only rebuilds when editAction/customButtons/deleteAction
  // actually change, instead of on every row/CD tick — so-row-actions-menu-ui already receives `item`
  // as its own input and calls `action.action(this.item())`, so these closures don't need to close over it.
  protected rowActionButtons = computed<SoTableButton[]>(() => {
    const buttons: SoTableButton[] = [];

    if (this.editAction()) {
      buttons.push({
        svg: 'edit',
        color: '--so-secondary',
        tooltip: 'Editar',
        action: (item: any) => this.editEvent.emit(item),
      });
    }

    buttons.push(...this.customButtons());

    if (this.deleteAction()) {
      buttons.push({
        svg: 'delete',
        color: '--so-danger',
        tooltip: 'Eliminar',
        variant: 'danger',
        action: (item: any) => this.deleteEvent.emit(item),
      });
    }

    return buttons;
  });

  onSwitchChange(item: Record<string, any>, status: boolean, event: Event): void {
    event.stopPropagation();
    const identifier = this.switchIdentifier();
    if (identifier) {
      item[identifier] = status;
    }
    this.selectedRow.set(item);
    this.switchEvent.emit({ data: item, status });
  }

  onSwitchChangeFromSwitch(item: Record<string, any>, status: boolean): void {
    const identifier = this.switchIdentifier();
    if (identifier) {
      item[identifier] = status;
    }
    this.selectedRow.set(item);
    this.switchEvent.emit({ data: item, status });
  }

  onSort(header: SoTableHeader): void {
    if (header.sortable === false) return;
    this.performSort(header.dataName, header.convertFunction);
  }

  isSwitchSortable(): boolean {
    return this.hasStickyColumns() && !!this.switchIdentifier();
  }

  onSwitchSort(): void {
    if (!this.isSwitchSortable()) return;
    this.performSort(this.switchIdentifier());
  }

  private performSort(field: string, convertFn?: (value: any, item?: any) => any): void {
    let direction: SoSortDirection;

    if (this.sortField() === field) {
      direction = this.sortDirection() === 'asc' ? 'desc' : 'asc';
    } else {
      direction = 'asc';
    }

    this.sortField.set(field);
    this.sortDirection.set(direction);
    this.applySort(field, direction, convertFn);
    this.sortChanged.emit({ field, direction });
  }

  onPageChange(page: number): void {
    this.internalCurrentPage.set(page);
    this.pageChanged.emit(page);
    this.refreshViewport();
  }

  onSearchChange(value: string): void {
    this.internalSearchText.set(value);
    this.internalCurrentPage.set(1);
    this.applySearch(value);
    if (this.searchDebounceId) {
      clearTimeout(this.searchDebounceId);
    }
    this.searchDebounceId = setTimeout(() => {
      this.tableSearchChanged.emit(value);
    }, 500);
  }

  onSave(): void {
    this.saveEvent.emit(this.internalData());
  }

  onLeftButtonClick(): void {
    this.leftButtonEvent.emit();
  }

  onTableActionButtonClick(): void {
    this.tableActionButtonClicked.emit();
  }

  // Navigation
  goToPreviousPage(): void {
    if (this.internalCurrentPage() > 1) {
      this.onPageChange(this.internalCurrentPage() - 1);
    }
  }

  goToNextPage(): void {
    if (this.internalCurrentPage() < this.totalPages()) {
      this.onPageChange(this.internalCurrentPage() + 1);
    }
  }

  // Private methods
  private cloneData(data: Record<string, any>[]): Record<string, any>[] {
    return JSON.parse(JSON.stringify(data ?? []));
  }

  private applySearch(searchTerm: string): void {
    this.filteredData.set(this.filterData(this.internalData(), searchTerm));
  }

  private applySort(field: string, direction: SoSortDirection, convertFn?: (value: any, item?: any) => any): void {
    if (!direction) return;

    const data = [...this.filteredData()];
    data.sort((a, b) => {
      let valueA = this.getPropertyValue(a, field);
      let valueB = this.getPropertyValue(b, field);

      if (convertFn) {
        valueA = convertFn(valueA, a);
        valueB = convertFn(valueB, b);
      }

      if (valueA == null) return direction === 'asc' ? 1 : -1;
      if (valueB == null) return direction === 'asc' ? -1 : 1;

      if (typeof valueA === 'string') valueA = valueA.toLowerCase();
      if (typeof valueB === 'string') valueB = valueB.toLowerCase();

      if (typeof valueA === 'boolean') {
        if (valueA > valueB) return direction === 'asc' ? -1 : 1;
        if (valueA < valueB) return direction === 'asc' ? 1 : -1;
      }

      if (valueA < valueB) return direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    this.filteredData.set(data);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.truncateCache = new WeakMap();
  }

  private normalizePageSize(value: number | string | boolean | null | undefined): number | null {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  }

  private clampPage(page: number, totalItems = this.filteredData().length, pageSize = this.itemsPerPage()): number {
    const normalizedPage = Number.isFinite(page) ? Math.max(1, Math.trunc(page)) : 1;
    const normalizedPageSize = this.normalizePageSize(pageSize);

    if (!normalizedPageSize) {
      return 1;
    }

    const totalPages = Math.ceil(totalItems / normalizedPageSize);
    if (totalPages === 0) {
      return 1;
    }

    return Math.min(normalizedPage, totalPages);
  }

  private filterData(data: Record<string, any>[], searchTerm: string): Record<string, any>[] {
    if (!searchTerm) {
      return [...data];
    }

    const term = searchTerm.toLowerCase();
    return data.filter(item =>
      Object.keys(item).some(key =>
        item[key] && item[key].toString().toLowerCase().includes(term)
      )
    );
  }

  // Variable Selection Headers methods
  private initializeHeaderSelections(headers: SoTableHeader[], selections: Record<string, SoSelectKey>): void {
    const newValues: Record<string, SoSelectKey> = {};
    this.previousVariableSelections.clear();

    headers.forEach(header => {
      const value = selections[header.dataName] ?? null;
      newValues[header.dataName] = value;
      this.previousVariableSelections.set(header.dataName, value);
    });

    this.headerSelectValues.set(newValues);
  }

  private initializeHeaderInputs(headers: SoTableHeader[]): void {
    const newValues: Record<string, string> = {};

    headers.forEach(header => {
      newValues[header.dataName] = header.header ?? '';
    });

    this.headerInputValues.set(newValues);
    this.validateHeaderInputs();
  }

  private validateHeaderInputs(): void {
    const values = this.headerInputValues();
    const errors: Record<string, boolean> = {};
    const seen = new Map<string, string>();

    Object.entries(values).forEach(([key, value]) => {
      const trimmed = value?.trim() ?? '';
      if (trimmed) {
        if (seen.has(trimmed)) {
          errors[key] = true;
          errors[seen.get(trimmed)!] = true;
        } else {
          seen.set(trimmed, key);
        }
      }
    });

    this.headerInputErrors.set(errors);
    const hasErrors = Object.keys(errors).length > 0;
    this.headersFormStatus.emit({ valid: !hasErrors, errors: hasErrors ? { nonUniqueInputs: true } : null });
  }

  protected isVariableStatusMode = computed(() => this.enableVariableStatus());

  protected isVariableSelectionMode = computed(() => this.enableVariableSelection() && !this.enableVariableStatus());

  getVariableStatus(dataName: string): SoTableVariableStatus | null {
    return this.variableStatusMap()[dataName] ?? null;
  }

  isColumnMapped(dataName: string): boolean {
    return !!this.getVariableStatus(dataName);
  }

  onColumnHovered(dataName: string | null): void {
    if (!this.enableVariableStatus()) return;
    this.columnHovered.emit(dataName);
  }

  scrollColumnIntoView(dataName: string): void {
    const container = this.tableContentRef?.nativeElement;
    if (!container) return;

    const target = container.querySelector<HTMLElement>(`th[data-col-id="${CSS.escape(dataName)}"]`);
    if (!target) return;

    const prefersReducedMotion = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      inline: 'center',
      block: 'nearest'
    });
  }

  getHeaderSelectValue(dataName: string): SoSelectKey {
    return this.headerSelectValues()[dataName] ?? null;
  }

  getHeaderInputValue(dataName: string): string {
    return this.headerInputValues()[dataName] ?? '';
  }

  hasHeaderInputError(dataName: string): boolean {
    return this.headerInputErrors()[dataName] ?? false;
  }

  onSelectVariable(keySelected: SoSelectKey, header: SoTableHeader): void {
    const dataName = header.dataName;
    const previousValue = this.previousVariableSelections.get(dataName);
    const hasPreviousValue = previousValue !== null && previousValue !== undefined && previousValue !== '';

    // Emit deselection if there was a previous value
    if (hasPreviousValue && previousValue !== keySelected) {
      this.variableDeselected.emit({
        column: dataName,
        variableKey: String(previousValue),
        headerText: header.header
      });
    }

    // Update internal state
    const newValues = { ...this.headerSelectValues() };
    newValues[dataName] = keySelected;
    this.headerSelectValues.set(newValues);
    this.previousVariableSelections.set(dataName, keySelected);

    // If cleared, don't emit selection
    if (!keySelected && keySelected !== 0) {
      return;
    }

    // Emit selection
    this.variableSelected.emit({
      column: dataName,
      variableKey: String(keySelected),
      headerText: header.header
    });
  }

  onHeaderInputChange(value: string, header: SoTableHeader): void {
    const dataName = header.dataName;
    const newValues = { ...this.headerInputValues() };
    newValues[dataName] = value;
    this.headerInputValues.set(newValues);

    this.validateHeaderInputs();

    this.variableHeaderEdited.emit({
      column: dataName,
      variableKey: String(this.headerSelectValues()[dataName] ?? ''),
      headerText: value
    });
  }

  // Track functions
  trackByIndex(index: number): number {
    return index;
  }

  trackByHeader(index: number, header: SoTableHeader): string {
    return header.dataName;
  }

  trackByRow = (index: number, item: Record<string, any>): any => {
    return item['id'] ?? item['key'] ?? index;
  };

  // Virtual scroll methods
  ngAfterViewInit(): void {
    this.viewportReady = true;
    this.refreshViewport();

    if (this.pendingViewportUpdate) {
      this.pendingViewportUpdate = false;
      this.refreshViewport();
    }

    if (this.hasStickyColumns())
      this.updateStickyOffsets();

    if (this.syncHorizontalScroll() || this.hasStickyColumns())
      this.setupHorizontalScrollSync();
  }

  ngOnDestroy(): void {
    this.theadResizeObserver?.disconnect();
    this.contentResizeObserver?.disconnect();
    this.renderedRangeSub?.unsubscribe();
    if (this.contentScrollListener && this.tableContentRef?.nativeElement) {
      this.tableContentRef.nativeElement.removeEventListener('scroll', this.contentScrollListener);
    }
  }

  protected refreshViewport(): void {
    if (this.viewportReady && this.viewport) {
      this.viewport.checkViewportSize();
      this.cdr.markForCheck();
    } else {
      this.pendingViewportUpdate = true;
    }
  }

  // returns the css syntax of the given value or variable
  protected cssValue(value: string | undefined): string | undefined {
    if (!value) return;
    return value.startsWith('--') ? `var(${ value })` : value;
  }

  protected isStickyRightOuter(key: string): boolean {
    return this.stickyRightOffsets()[key] === 0;
  }

  private updateStickyOffsets(): void {
    const thead = this.tableHeadRef?.nativeElement;
    const contentEl = this.tableContentRef?.nativeElement;
    if (!thead || !contentEl) return;

    const specialWidth = (headerSelector: string, cellSelector: string): number => {
      const el =
        (contentEl.querySelector(cellSelector) as HTMLElement | null) ??
        (thead.querySelector(headerSelector) as HTMLElement | null);
      return el?.getBoundingClientRect().width ?? 0;
    };
    const dataColumnWidth = (dataName: string): number =>
      thead.querySelector<HTMLElement>(`th[data-column-key="${dataName}"]`)?.getBoundingClientRect().width ?? 0;

    // Left side: actions (if sticky) then sticky data columns, in render order.
    const leftOffsets: Record<string, number> = {};
    let leftAcc = 0;
    if (this.isActionsStickyLeft()) {
      leftOffsets['actions'] = leftAcc;
      leftAcc += specialWidth('.so-table-actions-header', '.so-table-actions-cell');
    }
    const stickyLeftNames = this.stickyLeftDataNames();
    for (const header of this.shownHeaders()) {
      if (!stickyLeftNames.has(header.dataName)) continue;
      leftOffsets[header.dataName] = leftAcc;
      leftAcc += dataColumnWidth(header.dataName);
    }
    this.stickyLeftOffsets.set(leftOffsets);

    // Right side: actions (if sticky), then icons, then switch, then sticky data columns,
    // walking from the edge inward — actions render last in the DOM in this mode, so they
    // sit flush against the edge and everything else shifts left by their width.
    const rightOffsets: Record<string, number> = {};
    let rightAcc = 0;
    if (this.isActionsStickyRight()) {
      rightOffsets['actions'] = rightAcc;
      rightAcc += specialWidth('.so-table-actions-header', '.so-table-actions-cell');
    }
    if (this.isIconsStickyRight()) {
      rightOffsets['icons'] = rightAcc;
      rightAcc += specialWidth('.so-table-icons-header', '.so-table-icons-cell');
    }
    if (this.isSwitchStickyRight()) {
      rightOffsets['switch'] = rightAcc;
      rightAcc += specialWidth('.so-table-switch-header', '.so-table-switch-cell');
    }
    const stickyRightNames = this.stickyRightDataNames();
    for (let i = this.shownHeaders().length - 1; i >= 0; i--) {
      const header = this.shownHeaders()[i];
      if (!stickyRightNames.has(header.dataName)) continue;
      rightOffsets[header.dataName] = rightAcc;
      rightAcc += dataColumnWidth(header.dataName);
    }
    this.stickyRightOffsets.set(rightOffsets);

    const viewportEl = this.viewport?.elementRef?.nativeElement;
    const scrollbarWidth = viewportEl ? Math.max(0, viewportEl.offsetWidth - viewportEl.clientWidth) : 0;
    this.scrollbarWidthPx.set(scrollbarWidth);
    contentEl.style.setProperty('--so-scrollbar-width', `${scrollbarWidth}px`);
  }

  // Configura la sincronización del scroll horizontal cuando hay muchas columnas
  private setupHorizontalScrollSync(): void {
    const thead = this.tableHeadRef?.nativeElement;
    const tfoot = this.tfootRef?.nativeElement;
    const tfootTable = tfoot?.closest('.so-table-tfoot') as HTMLElement | null;
    const contentEl = this.tableContentRef?.nativeElement;
    const viewportEl = this.viewport?.elementRef?.nativeElement;

    if (!thead || !contentEl || !viewportEl) return;

    const contentWrapper = viewportEl.querySelector('.cdk-virtual-scroll-content-wrapper') as HTMLElement;
    const virtualTable = viewportEl.querySelector('.so-table-virtual') as HTMLElement;

    const syncColumnWidths = () => {
      const dataHeaderCells = Array.from(
        thead.querySelectorAll<HTMLTableCellElement>('th[data-column-key]')
      );
      const virtualTableRows = virtualTable?.querySelectorAll('tbody tr');

      if (!virtualTableRows || virtualTableRows.length === 0) return;

      const widthByColumnKey = new Map(
        dataHeaderCells
          .filter((th) => !!th.dataset['columnKey'])
          .map((th) => [th.dataset['columnKey']!, th.getBoundingClientRect().width] as const)
      );

      virtualTableRows.forEach((row) => {
        const bodyCells = Array.from(row.querySelectorAll<HTMLTableCellElement>('td[data-column-key]'));
        bodyCells.forEach((td) => {
          const columnKey = td.dataset['columnKey'];
          const width = columnKey ? widthByColumnKey.get(columnKey) : undefined;
          if (width !== undefined) {
            td.style.width = `${width}px`;
            td.style.minWidth = `${width}px`;
            td.style.maxWidth = `${width}px`;
          }
        });
      });

      // Switch/icons live in a separate <table> from their header (thead vs the virtual-scroll
      // body table), so table-layout: fixed sizes each independently and their widths can drift
      // apart even though both sides bind the same justify-content/switchAlign — the header text
      // and the cell content end up centered in two differently-sized boxes. Force the body cell
      // to the header cell's measured width, same as data columns above.
      const applySpecialCellWidth = (headerSelector: string, cellSelector: string) => {
        const headerEl = thead.querySelector<HTMLElement>(headerSelector);
        if (!headerEl) return;
        const width = headerEl.getBoundingClientRect().width;
        virtualTableRows.forEach((row) => {
          const cell = row.querySelector<HTMLElement>(cellSelector);
          if (cell) {
            cell.style.width = `${width}px`;
            cell.style.minWidth = `${width}px`;
            cell.style.maxWidth = `${width}px`;
          }
        });
      };
      applySpecialCellWidth('.so-table-switch-header', '.so-table-switch-cell');
      applySpecialCellWidth('.so-table-icons-header', '.so-table-icons-cell');

      this.updateStickyOffsets();

      // Sincronizar tfoot si existe
      if (tfoot) {
        const tfootCells = Array.from(tfoot.querySelectorAll<HTMLTableCellElement>('td[data-column-key]'));
        tfootCells.forEach((td) => {
          const columnKey = td.dataset['columnKey'];
          const width = columnKey ? widthByColumnKey.get(columnKey) : undefined;
          if (width !== undefined) {
            td.style.width = `${width}px`;
            td.style.minWidth = `${width}px`;
            td.style.maxWidth = `${width}px`;
          }
        });
      }

      this.stickyLeftSyncFn?.();
    };

    const syncStickyLeftTransforms = (scrollLeft?: number) => {
      if (!virtualTable) return;
      const offset = scrollLeft ?? contentEl.scrollLeft;
      const stickyLeftCells = virtualTable.querySelectorAll<HTMLElement>('td.sticky-left');
      stickyLeftCells.forEach((cell) => {
        cell.style.transform = `translateX(${offset}px)`;
      });
    };

    // Guardar referencias para uso posterior
    this.columnWidthSyncFn = syncColumnWidths;
    this.stickyLeftSyncFn = syncStickyLeftTransforms;

    // 1. Sincroniza el ancho del viewport y tfoot con el thead
    const syncWidth = () => {
      const theadWidth = thead.scrollWidth;
      if (contentWrapper) contentWrapper.style.minWidth = `${ theadWidth }px`;
      if (virtualTable) {
        // Ancho definido (no max-content) para que table-layout: fixed se respete siempre;
        // de lo contrario el navegador cae en auto-layout y una columna con contenido largo
        // sin puntos de corte (correos) ignora su ancho y desfasa las columnas siguientes.
        virtualTable.style.width = `${ theadWidth }px`;
        virtualTable.style.minWidth = `${ theadWidth }px`;
      }
      if (tfootTable) {
        tfootTable.style.width = `${ theadWidth }px`;
        tfootTable.style.minWidth = `${ theadWidth }px`;
      }

      // Sincronizar anchos de columnas después de sincronizar el ancho total
      setTimeout(() => syncColumnWidths(), 0);
    };
    syncWidth();
    this.theadResizeObserver = new ResizeObserver(syncWidth);
    this.theadResizeObserver.observe(thead);

    let rafScheduled = false;
    this.contentScrollListener = () => {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(() => {
        const scrollLeft = contentEl.scrollLeft;
        viewportEl.style.width = `calc(100% + ${ scrollLeft }px)`;
        syncStickyLeftTransforms(scrollLeft);
        rafScheduled = false;
      });
    };
    contentEl.addEventListener('scroll', this.contentScrollListener);

    this.renderedRangeSub = this.viewport?.renderedRangeStream.subscribe(() => {
      syncColumnWidths();
    });

    // 3. Resetea el scroll cuando el contenedor crece
    let previousContentWidth = contentEl.clientWidth;
    this.contentResizeObserver = new ResizeObserver(() => {
      const currentWidth = contentEl.clientWidth;
      if (currentWidth > previousContentWidth) {
        contentEl.scrollLeft = 0;
        viewportEl.style.width = '100%';
      }
      previousContentWidth = currentWidth;
    });
    this.contentResizeObserver.observe(contentEl);
  }
  getContentButtonText(item: any, header: SoTableHeader): string {
    if(typeof header.isButton!.text === 'function') return header.isButton!.text(item);
    else return header.isButton!.text ?? '';
  }

  getHighlightedParts(item: any, header: SoTableHeader): SoTableHighlightedText[] {
    const value = this.getPropertyValue(item, header.dataName);
    const config = header.highlightText!;

    if (config.isHighlightableFn && !config.isHighlightableFn(item))
      return [{ text: String(value ?? ''), highlighted: false }];

    const text = String(value ?? '');
    const match = config.regex.exec(text);

    if (!match || match[config.regexGroupIndex] === undefined)
      return [{ text, highlighted: false }];

    const groupMatch = match[config.regexGroupIndex];
    const fullMatch = match[0];
    const fullMatchIndex = match.index;

    const groupIndexInFullMatch = fullMatch.indexOf(groupMatch);
    const groupStartIndex = fullMatchIndex + groupIndexInFullMatch;
    const groupEndIndex = groupStartIndex + groupMatch.length;

    const toNbsp = (str: string) => str.replace(/ /g, ' ');
    const parts: SoTableHighlightedText[] = [];

    if (groupStartIndex > 0)
      parts.push({ text: toNbsp(text.substring(0, groupStartIndex)), highlighted: false });

    parts.push({ text: toNbsp(groupMatch), highlighted: true });

    if (groupEndIndex < text.length)
      parts.push({ text: toNbsp(text.substring(groupEndIndex)), highlighted: false });

    return parts;
  }

  onHighlightClick(item: any, header: SoTableHeader, event: Event): void {
    event.stopPropagation();
    if (header.highlightText?.action) {
      header.highlightText.action(item);
    }
  }

  isLinkVisible(header: SoTableHeader, item: any): boolean {
    const show = header.isLink?.show;
    if (show === undefined) return true;
    if (typeof show === 'function') return show(item);
    return show;
  }

  getLinkAriaLabel(header: SoTableHeader, item: any): string {
    const ariaLabel = header.isLink?.ariaLabel;
    if (ariaLabel) return ariaLabel(item);
    return String(this.getPropertyValue(item, header.dataName) ?? '');
  }

  onLinkClick(header: SoTableHeader, item: any, event: Event): void {
    event.stopPropagation();
    header.isLink?.action(item);
  }
}
